import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { AxiosError } from "axios";
import { enqueueSnackbar } from "notistack";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { prepareMoneyFlowRequestDto } from "@/api/hooks/stat-analytics-hooks";
import { DataOutdatedAlert } from "@/module/service-configs-module/statistics-visualization-module/components/data-outdated-alert";
import { SelectFilterMessage } from "@/module/service-configs-module/statistics-visualization-module/components/select-filter-message";
import { useGetTimings } from "@/module/service-configs-module/statistics-visualization-module/timings/hooks/use-get-timings";
import { TimingChart } from "@/module/service-configs-module/statistics-visualization-module/timings/timing-chart";
import { MoneyFlowRequestDto } from "@/shared/api";
import { useApi } from "@/shared/api/use-api";

type TimingsPageProps = {
  filters: MoneyFlowRequestDto | null;
  isDataOutdated: boolean;
};

const PERIOD_OPTIONS = [100, 500, 1000];

export const TimingsPage = ({ filters, isDataOutdated }: TimingsPageProps) => {
  const { statAnalyticsApi } = useApi();
  const [key, setKey] = useState<number | null>(null);
  const [period, setPeriod] = useState(PERIOD_OPTIONS[0]);
  const [periodFilter, setPeriodFilter] = useState<number | null>(null);
  const { isLoading, data } = useGetTimings(filters, periodFilter, key);
  const { t } = useTranslation();
  const [isExportingLoading, setIsExportingLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement | undefined>(undefined);

  const handleSubmit = () => {
    setPeriodFilter(period);
    setKey(+new Date());
  };

  const handleExport = async () => {
    if (isExportingLoading || !filters || !data) {
      return;
    }

    try {
      setIsExportingLoading(true);
      const allActions = data.reduce<string[]>((acc, item) => {
        item.data.forEach((val) => {
          acc.push(val.action);
        });
        return acc;
      }, []);

      const response = await statAnalyticsApi.exportTimings(
        {
          streets: data.map((item) => item.street),
          action_types: Array.from(new Set(allActions)),
          query: prepareMoneyFlowRequestDto(filters),
        },
        {
          responseType: "blob",
        }
      );
      const contentDisposition = response?.headers["content-disposition"];
      if (contentDisposition) {
        const fileName = contentDisposition.split("filename=")[1].split(";")[0];
        const url = URL.createObjectURL(response.data as Blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (e) {
      const message =
        (e as unknown as AxiosError<{ message: string }>)?.response?.data?.message || e;

      enqueueSnackbar(message as string, {
        variant: "error",
        autoHideDuration: 2000,
      });
    } finally {
      setIsExportingLoading(false);
    }
  };

  const maxChartWidth = containerRef.current?.clientWidth
    ? (containerRef.current?.clientWidth - 4 * 16) / 5
    : null;

  return (
    // @ts-expect-error valid
    <Stack sx={{ p: 2 }} ref={containerRef}>
      <DataOutdatedAlert isDataOutdated={isDataOutdated} />
      {!!filters && (
        <Box
          sx={{
            display: "flex",
            gap: 3,
            justifyContent: "flex-start",
            alignItems: "center",
            mb: 3,
          }}
        >
          <FormControl fullWidth sx={{ width: "200px" }}>
            <InputLabel size="small">{t("periodMs")}</InputLabel>
            <Select
              label={t("periodMs")}
              size="small"
              value={period}
              onChange={(e) => setPeriod(Number(e.target.value))}
            >
              {PERIOD_OPTIONS?.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={handleSubmit} loading={isLoading}>
            {t("submit")}
          </Button>
        </Box>
      )}

      {filters ? (
        <>
          {isLoading ? (
            <CircularProgress sx={{ margin: "96px auto 32px" }} />
          ) : (
            <Stack>
              {!!maxChartWidth && (
                <>
                  {!!data && (
                    <Button
                      variant="contained"
                      onClick={handleExport}
                      loading={isExportingLoading}
                      sx={{ position: "absolute", top: 0, right: "140px" }}
                    >
                      {t("exportAll")}
                    </Button>
                  )}
                  {!!periodFilter &&
                    data?.map((item, index) => {
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            gap: "16px",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            rowGap: 8,
                            columnGap: 1,
                            mb: 8,
                          }}
                          key={item.street}
                        >
                          {item.data.map((action) => {
                            return (
                              <Box
                                sx={{
                                  width: maxChartWidth,
                                  height: "300px",
                                }}
                                key={action.action}
                              >
                                <TimingChart
                                  action={action.action}
                                  street={item.street}
                                  data={action.timings}
                                  index={index}
                                  filters={filters}
                                  periodFilter={periodFilter}
                                />
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })}
                </>
              )}
            </Stack>
          )}
        </>
      ) : (
        <SelectFilterMessage />
      )}
    </Stack>
  );
};
